import { DatePipe, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ConnectIconComponent } from './connect-icon.component';
import {
  canLinkDetails,
  claimCardStatus,
  ClaimCardStatus,
  claimStaleness,
  ConnectClaimItem,
  formatDob,
  sastTodayIso,
  StalenessChip,
} from './connect-dashboard.util';

const LABELS: Record<ClaimCardStatus, string> = {
  approved: 'Approved',
  rejected: 'Rejected',
  'claim-partial': 'Partial',
  'paper-claim': 'Paper claim',
  submitted: 'Submitted',
  complete: 'Review',
  partial: 'Needs ICD',
  missing: 'Link details',
};

/** One submission from clinical waiting to go to the scheme, as Connect shows it. It opens read-only. */
@Component({
  selector: 'cx-claim-card',
  standalone: true,
  imports: [NgIf, DatePipe, ConnectIconComponent],
  host: { class: 'cx-card-host cx-card-host--claim' },
  template: `
    <section [class]="'cx-claim cx-claim--' + status" (click)="open.emit()">
      <div class="cx-claim-body">
        <div class="cx-card-top">
          <span class="cx-claim-name">{{ claim.patient.name }} {{ claim.patient.surname }}</span>
          <button *ngIf="linksDetails; else statusPill" type="button" class="cx-chip cx-chip--round cx-chip--action cx-claim-pill--missing"
            (click)="linkDetails.emit(); $event.stopPropagation()">{{ label }}</button>
          <ng-template #statusPill><span [class]="'cx-chip cx-chip--round cx-claim-pill--' + status">{{ label }}</span></ng-template>
        </div>
        <span class="cx-meta" *ngIf="claim.patient.accountNumber">Account no. {{ claim.patient.accountNumber }}</span>
        <span class="cx-meta" *ngIf="claim.patient.patientNumber">MedicAI file no. {{ claim.patient.patientNumber }}</span>
        <span class="cx-meta" *ngIf="claim.patient.idNumber">ID no. {{ claim.patient.idNumber }}</span>
        <span class="cx-meta" *ngIf="claim.patient.passportNumber && !claim.patient.idNumber">Passport no. {{ claim.patient.passportNumber }}</span>
        <span class="cx-meta" *ngIf="dob">DOB {{ dob }}</span>
        <span class="cx-meta" *ngIf="claim.patient.medicalAid">{{ claim.patient.medicalAid + ' ' + claim.patient.medicalAidPlan }}</span>
        <span class="cx-meta" *ngIf="claim.patient.medicalAidNumber">Member number: {{ claim.patient.medicalAidNumber }}</span>
        <span class="cx-meta cx-meta--strong" *ngIf="claim.hnetNumber">HNet #{{ claim.hnetNumber }}</span>
        <span class="cx-meta cx-meta--strong" *ngIf="claim.trackingId">Tracking {{ claim.trackingId }}</span>
        <span class="cx-meta cx-meta--dates">{{ claim.batch.startDate | date: 'dd/MM/yyyy' }}–{{ claim.batch.endDate | date: 'dd/MM/yyyy' }}</span>
        <span class="cx-chip cx-chip--block cx-chip--tight" *ngIf="staleness as stale"
          [class]="'cx-chip cx-chip--block cx-chip--tight cx-pill-' + (stale.tone === 'danger' ? 'danger' : 'warning')">{{ stale.label }}</span>
        <span class="cx-age">{{ claim.procedureCodeCount }} procedure · {{ claim.icdCodeCount }} ICD</span>
        <div class="cx-actions">
          <button type="button" class="cx-btn cx-btn--outline" (click)="open.emit(); $event.stopPropagation()">View</button>
          <button *ngIf="claim.patient.clinicalPatientId" type="button" class="cx-btn cx-btn--ghost" (click)="openPatientFile.emit(); $event.stopPropagation()">
            <cx-icon name="user"></cx-icon> Open patient file
          </button>
        </div>
      </div>
    </section>
  `,
})
export class ConnectClaimCardComponent {
  @Input() set claim(value: ConnectClaimItem) {
    this._claim = value;
    this.status = claimCardStatus(value);
    this.label = LABELS[this.status];
    this.linksDetails = canLinkDetails(value);
    this.dob = formatDob(value.patient.dob);
    const draft = ['complete', 'partial', 'missing'].includes(this.status);
    this.staleness = draft ? claimStaleness(value.maxProcedureDate, sastTodayIso()) : null;
  }
  get claim(): ConnectClaimItem {
    return this._claim;
  }
  private _claim!: ConnectClaimItem;

  /** The claim, read-only, in the patient's account. */
  @Output() open = new EventEmitter<void>();
  @Output() openPatientFile = new EventEmitter<void>();
  /** The patient's file, with the dialog that links the medical aid open. */
  @Output() linkDetails = new EventEmitter<void>();

  status: ClaimCardStatus = 'missing';
  label = '';
  linksDetails = false;
  dob: string | null = null;
  staleness: StalenessChip | null = null;
}
